import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { createQueryClient } from "@/shared/api/query-client";
import { installSessionHandlers } from "@/shared/auth/sessionHandlers";
import { useTheme } from "@/shared/hooks/useTheme";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { createAppRouter } from "@/routes";

/** sonner styled through our tokens (CSS variables it reads), never its default palette. */
const TOAST_STYLE = {
  "--normal-bg": "var(--surface-raised)",
  "--normal-text": "var(--text)",
  "--normal-border": "var(--border)",
  "--success-bg": "var(--success-soft)",
  "--success-text": "var(--success-fg)",
  "--success-border": "var(--success)",
  "--error-bg": "var(--danger-soft)",
  "--error-text": "var(--danger-fg)",
  "--error-border": "var(--danger)",
  "--warning-bg": "var(--warning-soft)",
  "--warning-text": "var(--warning-fg)",
  "--warning-border": "var(--warning)",
  "--border-radius": "var(--radius)",
  "--font-family": "var(--font-sans)",
} as CSSProperties;

function AppToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="bottom-right" closeButton style={TOAST_STYLE} />;
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(createAppRouter);

  // A definite 401 from the renewal → sign-in with the reason; a move → the new organisation with a
  // notice; another tab's move → follow it; a stale page → "Reload" (ADR-0011 addendum 2026-10-04).
  useEffect(() => installSessionHandlers(queryClient, router), [queryClient, router]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <AppToaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
